// Справочные карточки: компания, контакт, судно, сотрудник.
//
// ЗАЧЕМ ОНИ В ОДНОМ ФАЙЛЕ. Все четыре устроены одинаково: живут в состоянии
// `draft/active/archived`, номера не имеют, выпускать их некуда. Архив вместо удаления —
// не осторожность, а необходимость: судно продали, а акты по нему остались, и уволенный
// сотрудник остаётся автором половины журнала изменений.
//
// ЧТО ЗДЕСЬ ГЛАВНОЕ. Судно перестало быть контактом. Сегодня судно — это карточка контакта,
// у которой фамилия начинается со слова «Судно», а признак «это судно» вычисляется по
// строке; из-за этого на одной сущности висят и «Должность», и «Позывной сигнал». Разведя
// их, мы убираем из карточки человека всю судовую часть, а из судна — человеческую, не теряя
// ни одного поля. Разбор — `cir-core/МОДЕЛЬ_ДАННЫХ.md` §4.3.
//
// ЧЕГО ЗДЕСЬ НЕТ НАРОЧНО. У сотрудника — ФОТ, суточных, овертаймов, штрафов, госномеров
// личных авто и геолокации. Первое видит одна учётка владельца и решается правами, а не
// карточкой; остальное в зеркало не переносится вовсе (§5.2). Заводить такие поля «на
// будущее» нельзя: поле, которое существует, рано или поздно заполняют.

import { fmtDate } from '../../lib/format'
import { проверкаИНН, проверкаКПП, проверкаIMO, проверкаMMSI, проверкаПочты, проверкаТелефона } from '../validate'
import { пусто, строка, ярлык } from '../values'
import type { ОписаниеКарточки } from '../types'

import {
  ВИДЫ_КОМПАНИЙ,
  илиПрочерк,
  подписьВарианта,
  ПОДРАЗДЕЛЕНИЯ,
  РОЛИ,
  СТРАНЫ_ФЛАГА,
  ТИПЫ_СУДОВ,
} from './obshchee'

/** Фамилия и имя одной строкой: так человека и судно называют в списках и в поиске. */
const фио = (з: Record<string, unknown>): string =>
  [з.last_name, з.first_name, з.middle_name]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((s) => s !== '')
    .join(' ')

// ─────────────────────────── компания ───────────────────────────

export const КОМПАНИЯ: ОписаниеКарточки = {
  сущность: 'компания',
  один: 'компания',
  винительный: 'компанию',
  много: 'Компании',
  документ: false,
  путь: (id) => `/cards/company/${id}`,
  заголовок: (з) => илиПрочерк(строка(з, 'name')),

  показатели: [
    {
      код: 'инн',
      подпись: 'ИНН',
      значение: (з) => илиПрочерк(строка(з, 'inn')),
      // Без ИНН контрагента не найти ни в банке, ни в ЭДО, и счёт ему не выставить.
      тревога: (з) => пусто(з.inn),
    },
    {
      код: 'вид',
      подпись: 'Вид',
      значение: (з) => подписьВарианта(ВИДЫ_КОМПАНИЙ, з, 'kind'),
    },
    {
      код: 'ответственный',
      подпись: 'Ответственный',
      значение: (з) => ярлык(з, 'assigned_id')?.название ?? '—',
    },
  ],

  группы: [
    { код: 'кто', заголовок: 'Кто это', вкладка: 'основное' },
    {
      код: 'реквизиты',
      заголовок: 'Реквизиты',
      вкладка: 'основное',
      подсказка: 'Банковский счёт живёт отдельной карточкой, а не полем здесь: банк, записанный вместо контрагента, уже попадал из карточки прямо в счёт и в УПД.',
    },
    { код: 'связь', заголовок: 'Как связаться', вкладка: 'основное' },
    { код: 'ведение', заголовок: 'Кто ведёт', вкладка: 'связи' },
  ],

  поля: [
    {
      код: 'name',
      подпись: 'Название',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'по названию компанию ищут в поле-связи сделки',
      максДлина: 200,
    },
    {
      код: 'full_name',
      подпись: 'Полное название по уставу',
      вид: 'строка',
      группа: 'кто',
      подсказка: 'Ровно так, как в учредительных документах: это название уезжает в договор и в УПД.',
      максДлина: 400,
    },
    {
      код: 'kind',
      подпись: 'Вид',
      вид: 'список',
      варианты: ВИДЫ_КОМПАНИЙ,
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'заказчик и поставщик попадают в разные отчёты и в разные направления счетов',
    },
    {
      код: 'is_own',
      подпись: 'Наша компания',
      вид: 'флаг',
      группа: 'кто',
      подсказка: 'Наше юрлицо, от имени которого выставляются документы. Свободным текстом эта пометка уже расползлась на несколько написаний одной и той же компании.',
    },
    {
      код: 'inn',
      подпись: 'ИНН',
      вид: 'строка',
      группа: 'реквизиты',
      проверка: проверкаИНН,
      подсказка: 'Десять цифр у юрлица, двенадцать у ИП. Второго поля под ИНН больше нет — в Bitrix их было два, и они расходились.',
      максДлина: 12,
    },
    {
      код: 'kpp',
      подпись: 'КПП',
      вид: 'строка',
      группа: 'реквизиты',
      проверка: проверкаКПП,
      подсказка: 'Девять знаков. У ИП его нет вовсе — оставьте пустым, а не ставьте прочерк.',
      максДлина: 9,
    },
    {
      код: 'postal_address',
      подпись: 'Почтовый адрес для документов',
      вид: 'текст',
      группа: 'реквизиты',
      подсказка: 'Куда отправлять оригиналы. Юридический адрес сюда не годится, если письма туда не доходят.',
    },
    {
      код: 'email',
      подпись: 'Почта',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаПочты,
      подсказка: 'Общий ящик компании. Личную почту сотрудника заводите в его карточке контакта.',
    },
    {
      код: 'phone',
      подпись: 'Телефон',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаТелефона,
    },
    {
      код: 'assigned_id',
      подпись: 'Ответственный',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'ведение',
      подсказка: 'Отсюда ответственный подставляется в новую сделку этого заказчика.',
    },
  ],

  обратные: [
    {
      код: 'сделки',
      заголовок: 'Сделки заказчика',
      сущность: 'сделка',
      поле: 'company_id',
      колонки: [
        { код: 'title', подпись: 'Название' },
        { код: 'stage_id', подпись: 'Стадия' },
        { код: 'amount', подпись: 'Сумма', число: true },
        { код: 'opened_at', подпись: 'Открыта' },
      ],
    },
    {
      код: 'счета',
      заголовок: 'Счета',
      сущность: 'счёт',
      поле: 'company_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'issued_at', подпись: 'Выставлен' },
        { код: 'amount', подпись: 'Сумма', число: true },
        { код: 'paid_at', подпись: 'Оплачен' },
      ],
    },
    {
      код: 'договоры',
      заголовок: 'Договоры',
      сущность: 'договор',
      поле: 'company_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'kind', подпись: 'Вид' },
        { код: 'signed_at', подпись: 'Подписан' },
        { код: 'valid_to', подпись: 'Действует до' },
      ],
    },
  ],
}

// ─────────────────────────── контакт ───────────────────────────

export const КОНТАКТ: ОписаниеКарточки = {
  сущность: 'контакт',
  один: 'контакт',
  винительный: 'контакт',
  много: 'Контакты',
  документ: false,
  путь: (id) => `/cards/person/${id}`,
  заголовок: (з) => илиПрочерк(фио(з)),

  показатели: [
    {
      код: 'компания',
      подпись: 'Компания',
      значение: (з) => ярлык(з, 'company_id')?.название ?? '—',
    },
    {
      код: 'должность',
      подпись: 'Должность',
      значение: (з) => илиПрочерк(строка(з, 'position')),
    },
    {
      код: 'телефон',
      подпись: 'Телефон',
      значение: (з) => илиПрочерк(строка(з, 'phone')),
      // Контакт, до которого нельзя дозвониться, бесполезен ровно в тот момент,
      // когда судно уже у причала.
      тревога: (з) => пусто(з.phone) && пусто(з.email),
    },
  ],

  группы: [
    {
      код: 'кто',
      заголовок: 'Кто это',
      вкладка: 'основное',
      подсказка: 'Только человек. Судно теперь отдельная карточка, и заводить его здесь больше не нужно.',
    },
    { код: 'работа', заголовок: 'Где работает', вкладка: 'основное' },
    { код: 'связь', заголовок: 'Как связаться', вкладка: 'основное' },
  ],

  поля: [
    {
      код: 'last_name',
      подпись: 'Фамилия',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'по фамилии контакт ищут — имени без фамилии в поиске не хватает',
      максДлина: 100,
    },
    {
      код: 'first_name',
      подпись: 'Имя',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'обращение в письме собирается из имени и отчества',
      максДлина: 100,
    },
    { код: 'middle_name', подпись: 'Отчество', вид: 'строка', группа: 'кто', максДлина: 100 },
    {
      код: 'company_id',
      подпись: 'Компания',
      вид: 'связь',
      связьС: 'компания',
      группа: 'работа',
      подсказка: 'Где человек работает. Пусто — это «не знаем», а не «компании нет»: нуля вместо связи в новой модели не бывает.',
    },
    {
      код: 'position',
      подпись: 'Должность',
      вид: 'строка',
      группа: 'работа',
      подсказкаВвода: 'Капитан, старший механик, снабженец',
      максДлина: 150,
    },
    {
      код: 'email',
      подпись: 'Почта',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаПочты,
      подсказка: 'На неё уходят счета и акты. Ошибка в домене видна только через неделю молчания.',
    },
    {
      код: 'phone',
      подпись: 'Телефон',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаТелефона,
      подсказкаВвода: '+7 921 000-00-00',
    },
  ],

  обратные: [
    {
      код: 'сделки',
      заголовок: 'Сделки, где он контактное лицо',
      сущность: 'сделка',
      поле: 'person_id',
      колонки: [
        { код: 'title', подпись: 'Название' },
        { код: 'stage_id', подпись: 'Стадия' },
        { код: 'amount', подпись: 'Сумма', число: true },
      ],
    },
  ],
}

// ─────────────────────────── судно ───────────────────────────

export const СУДНО: ОписаниеКарточки = {
  сущность: 'судно',
  один: 'судно',
  винительный: 'судно',
  много: 'Суда',
  документ: false,
  путь: (id) => `/cards/vessel/${id}`,
  заголовок: (з) => илиПрочерк(строка(з, 'name')),

  показатели: [
    {
      код: 'imo',
      подпись: 'IMO',
      значение: (з) => илиПрочерк(строка(з, 'imo')),
      // IMO — единственный опознаватель судна на весь мир. Без него история работ
      // рассыпается на карточки-тёзки, а их в справочнике уже накопилось.
      тревога: (з) => пусто(з.imo),
    },
    {
      код: 'флаг',
      подпись: 'Флаг',
      значение: (з) => подписьВарианта(СТРАНЫ_ФЛАГА, з, 'flag_id'),
    },
    {
      код: 'владелец',
      подпись: 'Судовладелец',
      значение: (з) => ярлык(з, 'owner_company_id')?.название ?? '—',
    },
  ],

  группы: [
    { код: 'что', заголовок: 'Что за судно', вкладка: 'основное' },
    {
      код: 'опознание',
      заголовок: 'Как опознаётся',
      вкладка: 'основное',
      подсказка: 'IMO и MMSI — разные номера разной длины. Девятизначное число в поле IMO означает, что туда положили MMSI, и такое значение здесь не сохранится.',
    },
    { код: 'приписка', заголовок: 'Флаг и порт', вкладка: 'основное' },
    { код: 'чьё', заголовок: 'Чьё судно', вкладка: 'связи' },
  ],

  поля: [
    {
      код: 'name',
      подпись: 'Название',
      вид: 'строка',
      группа: 'что',
      обязательно: true,
      почемуОбязательно: 'без названия судно не найти ни в поиске, ни в акте',
      максДлина: 200,
    },
    {
      код: 'vessel_type_id',
      подпись: 'Тип судна',
      вид: 'список',
      варианты: ТИПЫ_СУДОВ,
      группа: 'что',
    },
    {
      код: 'imo',
      подпись: 'IMO',
      вид: 'строка',
      группа: 'опознание',
      проверка: проверкаIMO,
      подсказка: 'Семь цифр, последняя — контрольная. Ошибка в одной цифре даёт чужое судно, поэтому номер проверяется прямо здесь, а не при сохранении.',
      максДлина: 7,
    },
    {
      код: 'mmsi',
      подпись: 'MMSI',
      вид: 'строка',
      группа: 'опознание',
      проверка: проверкаMMSI,
      подсказка: 'Девять цифр. Меняется при смене флага — в отличие от IMO, который у судна один на всю жизнь.',
      максДлина: 9,
    },
    {
      код: 'call_sign',
      подпись: 'Позывной сигнал',
      вид: 'строка',
      группа: 'опознание',
      максДлина: 16,
    },
    {
      код: 'flag_id',
      подпись: 'Флаг',
      вид: 'список',
      варианты: СТРАНЫ_ФЛАГА,
      группа: 'приписка',
      подсказка: 'Выбор из справочника стран, а не текст: одна и та же страна, записанная по-разному, уже развалила отчёт по флагам.',
    },
    {
      код: 'home_port',
      подпись: 'Порт приписки',
      вид: 'строка',
      группа: 'приписка',
      максДлина: 120,
    },
    {
      код: 'owner_company_id',
      подпись: 'Судовладелец',
      вид: 'связь',
      связьС: 'компания',
      группа: 'чьё',
      подсказка: 'Отсюда заказчик подставляется в новую сделку по этому судну. Платить может и не владелец — тогда заказчика в сделке меняют руками.',
    },
  ],

  обратные: [
    {
      код: 'акты',
      заголовок: 'Акты по судну',
      сущность: 'акт',
      поле: 'vessel_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'act_date', подпись: 'Дата' },
        { код: 'place', подпись: 'Место' },
        { код: 'hours_total', подпись: 'Часы', число: true },
      ],
    },
    {
      код: 'проверки',
      заголовок: 'Проверки оборудования',
      сущность: 'проверка',
      поле: 'vessel_id',
      колонки: [
        { код: 'kind_id', подпись: 'Что проверяли' },
        { код: 'performed_at', подпись: 'Когда' },
        { код: 'result', подпись: 'Результат' },
        { код: 'next_due_at', подпись: 'Следующая' },
      ],
    },
    {
      код: 'сделки',
      заголовок: 'Работы по судну',
      сущность: 'сделка',
      поле: 'vessel_id',
      колонки: [
        { код: 'title', подпись: 'Название' },
        { код: 'stage_id', подпись: 'Стадия' },
        { код: 'amount', подпись: 'Сумма', число: true },
        { код: 'opened_at', подпись: 'Открыта' },
      ],
    },
  ],
}

// ─────────────────────────── сотрудник ───────────────────────────

export const СОТРУДНИК: ОписаниеКарточки = {
  сущность: 'сотрудник',
  один: 'сотрудник',
  винительный: 'сотрудника',
  много: 'Сотрудники',
  документ: false,
  путь: (id) => `/cards/employee/${id}`,
  заголовок: (з) => илиПрочерк(фио(з)),

  показатели: [
    {
      код: 'подразделение',
      подпись: 'Подразделение',
      значение: (з) => подписьВарианта(ПОДРАЗДЕЛЕНИЯ, з, 'org_unit_id'),
    },
    {
      код: 'роль',
      подпись: 'Роль',
      значение: (з) => подписьВарианта(РОЛИ, з, 'role'),
    },
    {
      код: 'работает',
      подпись: 'Работает',
      значение: (з) => {
        if (з.active === true) return 'Да'
        const уволен = строка(з, 'fired_at')
        return уволен === '' ? 'Нет' : `Уволен ${fmtDate(уволен)}`
      },
      тревога: (з) => з.active !== true,
    },
  ],

  группы: [
    { код: 'кто', заголовок: 'Кто это', вкладка: 'основное' },
    { код: 'связь', заголовок: 'Как связаться', вкладка: 'основное' },
    { код: 'место', заголовок: 'Где работает', вкладка: 'основное' },
    {
      код: 'доступ',
      заголовок: 'Работа в системе',
      вкладка: 'основное',
      подсказка: 'Уволенного не удаляют, а гасят: он остаётся автором половины журнала изменений, и без него история читается как анонимная.',
    },
  ],

  поля: [
    {
      код: 'last_name',
      подпись: 'Фамилия',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'фамилия стоит в акте и в журнале изменений',
      максДлина: 100,
    },
    {
      код: 'first_name',
      подпись: 'Имя',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'без имени сотрудника не отличить от однофамильца',
      максДлина: 100,
    },
    { код: 'middle_name', подпись: 'Отчество', вид: 'строка', группа: 'кто', максДлина: 100 },
    {
      код: 'email',
      подпись: 'Рабочая почта',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаПочты,
      подсказка: 'На неё уходят уведомления системы. Она же обязана быть уникальной — по ней сводятся учётки.',
    },
    {
      код: 'phone',
      подпись: 'Телефон',
      вид: 'строка',
      группа: 'связь',
      проверка: проверкаТелефона,
    },
    {
      код: 'org_unit_id',
      подпись: 'Подразделение',
      вид: 'список',
      варианты: ПОДРАЗДЕЛЕНИЯ,
      группа: 'место',
      обязательно: true,
      почемуОбязательно: 'подразделение подставляется из сотрудника в его сделки и делит по регионам выручку',
    },
    {
      код: 'role',
      подпись: 'Роль',
      вид: 'список',
      варианты: РОЛИ,
      группа: 'место',
      обязательно: true,
      почемуОбязательно: 'роль решает, что человек видит в системе',
      подсказка: 'Роль — не должность: это набор прав. Разделы с деньгами открыты по учётке, а не по роли, поэтому роль администратора доступа к ним не даёт.',
    },
    {
      код: 'login',
      подпись: 'Логин',
      вид: 'строка',
      группа: 'доступ',
      обязательно: true,
      почемуОбязательно: 'по логину человек входит и по нему же подписаны его правки',
      максДлина: 64,
    },
    {
      код: 'active',
      подпись: 'Работает',
      вид: 'флаг',
      группа: 'доступ',
      подсказка: 'Снятая галочка закрывает вход, но карточку и все ссылки на неё оставляет на месте.',
    },
    { код: 'hired_at', подпись: 'Принят', вид: 'дата', группа: 'доступ' },
    {
      код: 'fired_at',
      подпись: 'Уволен',
      вид: 'дата',
      группа: 'доступ',
      подсказка: 'Дата увольнения, а не признак: по ней видно, мог ли человек править карточку в спорный день.',
    },
  ],

  обратные: [
    {
      код: 'сделки',
      заголовок: 'Сделки, где он ответственный',
      сущность: 'сделка',
      поле: 'assigned_id',
      колонки: [
        { код: 'title', подпись: 'Название' },
        { код: 'stage_id', подпись: 'Стадия' },
        { код: 'amount', подпись: 'Сумма', число: true },
      ],
    },
  ],
}
